import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import type { ChatCanvas, ChatCanvasVersion } from '@kennisbank/shared';
import {
  exportCanvas,
  fetchCanvas,
  fetchCanvasVersions,
  restoreCanvasVersion,
} from '../api/canvas.js';
import { ChevronDownIcon } from './icons.js';
import { CodeBlock } from './CodeBlock.js';
import { sanitizeAtlassianWiki } from '../lib/atlassian-markup.js';

/**
 * Replace inline `[chunkId: <id>(, <id>)*]` markers with `[N]` superscript
 * anchors that link to `#cite-<n>` at the bottom of the canvas. Mirrors
 * `AnswerMarkdown` in KnowledgeAgent.tsx so canvas and chat render the
 * same shorthand. Unknown chunkIds (not in the snapshot) are dropped
 * silently — they shouldn't survive the citation validator, but be
 * defensive.
 */
function rewriteChunkIdMarkers(
  text: string,
  citationSnapshot: ChatCanvas['citationSnapshot'],
): string {
  const indexByChunkId = new Map<string, number>();
  citationSnapshot.forEach((c, i) => indexByChunkId.set(c.chunkId, i + 1));
  return text.replace(/\[chunkId:\s*([^\]]+)\]/g, (_match, raw: string) => {
    const ids = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const numbers = ids
      .map((id) => indexByChunkId.get(id))
      .filter((n): n is number => typeof n === 'number');
    if (numbers.length === 0) return '';
    const label = numbers.map((n) => `\\[${n}\\]`).join('');
    return `[${label}](#cite-${numbers[0] ?? ''})`;
  });
}

/**
 * CanvasPane (issue #3) — read-only right-side pane that renders the
 * current canvas markdown for the active chat. Surfaces an Export menu
 * (docx / pptx) and a version dropdown with a "Restore as new version"
 * affordance. Direct edit is deferred to v1.5.
 *
 * Mounting contract: parent renders this only when the canvas exists for
 * the active chat AND `flags.canvasEnabled` is true. On chat switch,
 * parent unmounts/remounts so we re-fetch.
 *
 * Locking: when `canvas.lockedUntil > now`, the pane shows a "Updating
 * canvas…" indicator and disables export/restore. Lock state polls every
 * 3s while held.
 */

export interface CanvasPaneProps {
  chatId: string;
  /** Optional initial canvas — parent can pass to avoid a flash on mount. */
  initialCanvas?: ChatCanvas | null;
  /** Notification hook so parent can refresh chat history when restore writes a new version. */
  onCanvasChanged?: (canvas: ChatCanvas) => void;
  /**
   * Called whenever the pane's canvas-or-null state changes (on initial
   * load, restore, or refresh). Parent uses it to drive the
   * "Add to canvas" affordance on chat answers — only visible when a
   * canvas exists for the active chat.
   */
  onCanvasStateChange?: (canvas: ChatCanvas | null) => void;
  /**
   * Pane width in pixels. Parent owns this so the resize handle between
   * chat content and the pane can drive both sides of the layout. Falls
   * back to 480 (the original fixed width).
   */
  width?: number;
  /**
   * Refresh trigger — when this value changes, the pane re-fetches
   * `/canvas`. Parent bumps it after triggering a canvas update via the
   * chat flow so the pane reflects the new version without polling.
   */
  refreshKey?: number;
}

type RestoreMessageId = `msg_${string}`;

// The stored intent is classifier vocabulary; the Versions menu shows
// what happened in words a user recognises. 'initial' covers both the
// first turn and a restore (restores are written as a fresh baseline).
const VERSION_INTENT_LABEL: Record<ChatCanvasVersion['intent'], string> = {
  initial: 'created / restored',
  structural: 'edited',
  content: 'expanded',
  mixed: 'edited + expanded',
};

export function CanvasPane({
  chatId,
  initialCanvas,
  onCanvasChanged,
  onCanvasStateChange,
  width = 480,
  refreshKey = 0,
}: CanvasPaneProps) {
  const [canvas, setCanvas] = useState<ChatCanvas | null>(initialCanvas ?? null);
  const [versions, setVersions] = useState<ChatCanvasVersion[] | null>(null);
  const [versionDropdownOpen, setVersionDropdownOpen] = useState(false);
  const [selectedVersionV, setSelectedVersionV] = useState<number | null>(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [errorCopy, setErrorCopy] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Fetch on chatId change or when parent bumps refreshKey.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchCanvas(chatId);
        if (cancelled) return;
        setCanvas(res.canvas);
        onCanvasStateChange?.(res.canvas);
      } catch {
        // 404 is expected when no canvas exists. Don't surface as error;
        // the parent will hide the pane.
        if (cancelled) return;
        setCanvas(null);
        onCanvasStateChange?.(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Note: onCanvasStateChange intentionally NOT in deps — parent owns
    // its identity stability; including it would re-fire on every parent
    // render. The project's eslint config doesn't register
    // react-hooks/exhaustive-deps so no disable directive is needed.
  }, [chatId, refreshKey]);

  // Poll lock state while a turn is in flight.
  useEffect(() => {
    if (!canvas?.lockedUntil) return;
    const lockTime = new Date(canvas.lockedUntil).getTime();
    if (lockTime <= Date.now()) return;
    const id = setInterval(async () => {
      try {
        const res = await fetchCanvas(chatId);
        setCanvas(res.canvas);
        if (!res.canvas.lockedUntil) clearInterval(id);
      } catch {
        clearInterval(id);
      }
    }, 3000);
    return () => clearInterval(id);
  }, [chatId, canvas?.lockedUntil]);

  if (!canvas) return null;

  const isLocked =
    canvas.lockedUntil !== null && new Date(canvas.lockedUntil).getTime() > Date.now();

  const renderedMarkdown =
    selectedVersionV !== null
      ? (versions?.find((v) => v.v === selectedVersionV)?.markdownContent ?? canvas.markdownContent)
      : canvas.markdownContent;

  async function ensureVersions() {
    if (versions) return versions;
    try {
      const res = await fetchCanvasVersions(chatId);
      setVersions(res.versions);
      return res.versions;
    } catch (err) {
      setErrorCopy(err instanceof Error ? err.message : 'Could not load version history.');
      return null;
    }
  }

  async function handleExport(format: 'docx' | 'pptx') {
    setExportMenuOpen(false);
    setBusy(true);
    setErrorCopy(null);
    try {
      const { blob, filename } = await exportCanvas(chatId, format);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorCopy(err instanceof Error ? err.message : 'Export failed.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRestore(v: number) {
    setBusy(true);
    setErrorCopy(null);
    try {
      // The fromTurn messageId is required by the route. In the absence
      // of a "current restore turn" we synthesise a deterministic one
      // tagged with the version + timestamp — the server stores it on
      // versions/{v}.fromTurn for audit-trail purposes.
      const synthetic: RestoreMessageId = `msg_restore${v}t${Date.now()}`;
      const res = await restoreCanvasVersion(chatId, v, synthetic);
      setCanvas(res.canvas);
      setSelectedVersionV(null);
      setVersionDropdownOpen(false);
      onCanvasChanged?.(res.canvas);
    } catch (err) {
      setErrorCopy(err instanceof Error ? err.message : 'Restore failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside
      aria-label="Canvas"
      style={{ width: `${width}px` }}
      className="flex h-full shrink-0 flex-col border-l border-nebula-gray-5 bg-white"
    >
      <header className="flex items-center justify-between gap-2 border-b border-nebula-gray-5 px-4 py-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-display text-sm font-semibold text-nebula-primary">Canvas</span>
            <span className="rounded-full bg-nebula-tint px-2 py-0.5 text-[10px] uppercase tracking-wide text-nebula-secondary">
              v{canvas.version}
            </span>
            {isLocked && <span className="text-[11px] text-nebula-fg-1/60">Updating canvas…</span>}
          </div>
          {canvas.starterTemplate && canvas.starterTemplate !== 'blank' && (
            <p className="mt-0.5 text-[11px] text-nebula-fg-1/60">
              Starter: {canvas.starterTemplate.replace(/_/g, ' ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Versions dropdown */}
          <div className="relative">
            <button
              type="button"
              disabled={busy || isLocked}
              onClick={async () => {
                if (!versionDropdownOpen) await ensureVersions();
                setVersionDropdownOpen((v) => !v);
                setExportMenuOpen(false);
              }}
              className="inline-flex items-center gap-1 rounded border border-nebula-gray-5 px-2 py-1 text-xs text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-50"
            >
              Versions
              <ChevronDownIcon size={12} className="text-nebula-fg-3" />
            </button>
            {versionDropdownOpen && versions && (
              <div className="absolute right-0 top-full z-10 mt-1 w-[220px] rounded border border-nebula-gray-5 bg-white shadow-lg">
                <ul className="max-h-[260px] overflow-y-auto py-1 text-xs">
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedVersionV(null);
                        setVersionDropdownOpen(false);
                      }}
                      className={`block w-full px-3 py-1.5 text-left hover:bg-nebula-tint ${
                        selectedVersionV === null ? 'bg-nebula-tint font-medium' : ''
                      }`}
                    >
                      Current (v{canvas.version})
                    </button>
                  </li>
                  {versions.map((v) => (
                    <li
                      key={v.v}
                      className="flex items-center justify-between gap-1 px-3 py-1.5 hover:bg-nebula-tint"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedVersionV(v.v);
                          setVersionDropdownOpen(false);
                        }}
                        className={`flex-1 text-left ${selectedVersionV === v.v ? 'font-medium' : ''}`}
                      >
                        v{v.v}{' '}
                        <span className="text-nebula-fg-1/50">
                          · {VERSION_INTENT_LABEL[v.intent]}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRestore(v.v)}
                        className="text-[10px] text-nebula-secondary hover:underline disabled:opacity-50"
                        disabled={busy}
                      >
                        Restore
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {/* Export menu */}
          <div className="relative">
            <button
              type="button"
              disabled={busy || isLocked}
              onClick={() => {
                setExportMenuOpen((v) => !v);
                setVersionDropdownOpen(false);
              }}
              className="inline-flex items-center gap-1 rounded bg-nebula-primary px-3 py-1 text-xs font-medium text-white hover:bg-nebula-secondary disabled:opacity-50"
            >
              Export
              <ChevronDownIcon size={12} className="text-white/80" />
            </button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full z-10 mt-1 w-[140px] rounded border border-nebula-gray-5 bg-white shadow-lg">
                <button
                  type="button"
                  onClick={() => handleExport('docx')}
                  className="block w-full px-3 py-2 text-left text-xs hover:bg-nebula-tint"
                >
                  Download as Word
                </button>
                <button
                  type="button"
                  onClick={() => handleExport('pptx')}
                  className="block w-full px-3 py-2 text-left text-xs hover:bg-nebula-tint"
                >
                  Download as PowerPoint
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      {errorCopy && (
        <div className="border-b border-nebula-error/30 bg-nebula-error-bg px-4 py-2 text-xs text-nebula-error">
          {errorCopy}
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {selectedVersionV !== null && (
          <p className="mb-3 rounded bg-nebula-tint px-2 py-1 text-[11px] text-nebula-secondary">
            Previewing v{selectedVersionV} (read-only). Export always uses the current version —
            click Restore next to v{selectedVersionV} in the Versions menu to make it current.
          </p>
        )}
        <article className="prose prose-sm max-w-none text-nebula-fg-1">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, rehypeSanitize]}
            components={{
              code: CodeBlock,
              a: ({ href, children }) => {
                if (typeof href === 'string' && href.startsWith('#cite-')) {
                  // Inline citation superscript — click scrolls to the
                  // matching entry in the Sources section below.
                  const target = href.slice('#cite-'.length);
                  return (
                    <sup className="ml-0.5 align-baseline text-[10px] font-medium text-nebula-secondary">
                      <a
                        href={href}
                        onClick={(e) => {
                          e.preventDefault();
                          const el = document.getElementById(`cite-${target}`);
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }}
                        className="cursor-pointer hover:underline"
                      >
                        {children}
                      </a>
                    </sup>
                  );
                }
                return (
                  <a href={href} target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                );
              },
              img: ({ src, alt }) => (
                <img
                  src={typeof src === 'string' ? src : ''}
                  alt={alt ?? ''}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  style={{ maxWidth: '100%', height: 'auto', borderRadius: 6 }}
                />
              ),
            }}
          >
            {sanitizeAtlassianWiki(
              rewriteChunkIdMarkers(renderedMarkdown, canvas.citationSnapshot),
            )}
          </ReactMarkdown>

          {canvas.citationSnapshot.length > 0 && (
            <section className="mt-8 border-t border-nebula-gray-5 pt-4">
              <h3 className="text-sm font-semibold text-nebula-fg-1">Sources</h3>
              <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-xs text-nebula-fg-1/80">
                {canvas.citationSnapshot.map((c, i) => (
                  <li key={c.chunkId} id={`cite-${i + 1}`} className="break-words">
                    <span className="font-medium">{c.title}</span>
                    {c.author && c.author !== 'Unknown' && (
                      <span className="text-nebula-fg-3"> · {c.author}</span>
                    )}
                    {typeof c.page === 'number' && c.page > 0 && (
                      <span className="text-nebula-fg-3"> · p.{c.page}</span>
                    )}
                    {c.sourceUrl && (
                      <a
                        href={c.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 text-nebula-secondary hover:underline"
                      >
                        open
                      </a>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          )}
        </article>
      </div>
    </aside>
  );
}
